import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, adminAssessmentAPI } from '../../services/api';
import UserManagement from '../UserManagement';
import EmployerRequests from '../EmployerRequests';
import AdminAssessmentManagement from '../AdminAssessmentManagement';
import AdminReportsManagement from '../AdminReportsManagement';
import CandidateSkillAssignment from '../CandidateSkillAssignment';
import EmployerSubscriptionPlans from '../EmployerSubscriptionPlans';
import CandidateDetailRequests from '../CandidateDetailRequests';
import AdminCourseManagement from '../AdminCourseManagement';

const MAX_COURSE_GENERATION_JOBS = 12;

const AdminDashboard = ({ user }) => {
  const [generationJobs, setGenerationJobs] = useState([]);

  const updateGenerationJob = useCallback((jobId, patch) => {
    setGenerationJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j)));
  }, []);

  const dismissGenerationJob = useCallback((jobId) => {
    setGenerationJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  const appendGenerationJob = useCallback((job) => {
    setGenerationJobs((prev) => {
      const next = [job, ...prev];
      return next.slice(0, MAX_COURSE_GENERATION_JOBS);
    });
  }, []);
  const [adminView, setAdminView] = useState('overview');
  const [kipMetrics, setKipMetrics] = useState({
    totalUsers: 0,
    activeCandidates: 0,
    totalAssessments: 0,
    pendingRequests: 0,
    isLoading: true
  });
  const [employersList, setEmployersList] = useState([]);
  const [loadingEmployersList, setLoadingEmployersList] = useState(false);
  const [aiSubscription, setAiSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  // Fetch admin KIP metrics
  useEffect(() => {
    const fetchKipMetrics = async () => {
      if (user?.userType === 'admin' && adminView === 'overview') {
        try {
          setKipMetrics(prev => ({ ...prev, isLoading: true }));
          
          // Fetch user stats (total candidates and employers/employees)
          let totalUsers = 0;
          let activeCandidates = 0;
          try {
            const userStatsResponse = await adminAPI.getUserStats();
            const userStats = userStatsResponse.data;
            
            // Use totalUsers from stats if available, otherwise calculate from candidates + employers
            if (userStats.totalUsers !== undefined) {
              totalUsers = userStats.totalUsers || 0;
            } else if (userStats.totalCandidates !== undefined && userStats.totalEmployers !== undefined) {
              totalUsers = (userStats.totalCandidates || 0) + (userStats.totalEmployers || 0);
            } else {
              // Fallback: fetch candidates and employers separately to get totals
              const [candidatesResponse, employersResponse] = await Promise.all([
                adminAPI.getCandidates(1, 1, '').catch(() => ({ data: { total: 0, totalPages: 0 } })),
                adminAPI.getEmployers(1, 1, '').catch(() => ({ data: { total: 0, totalPages: 0 } }))
              ]);
              totalUsers = (candidatesResponse.data.total || 0) + (employersResponse.data.total || 0);
            }
            
            // Fetch active candidates count
            // Try to get from stats first, otherwise fetch and filter
            if (userStats.activeCandidates !== undefined) {
              activeCandidates = userStats.activeCandidates || 0;
            } else {
              // Fetch candidates list and count active ones
              try {
                const candidatesListResponse = await adminAPI.getCandidates(1, 1000, '');
                const candidates = candidatesListResponse.data.candidates || [];
                activeCandidates = candidates.filter(c => c.isActive !== false).length;
              } catch (error) {
                console.error('Error fetching candidates for active count:', error);
                activeCandidates = 0;
              }
            }
          } catch (error) {
            console.error('Error fetching user stats:', error);
          }

          // Fetch total assessments count
          let totalAssessments = 0;
          try {
            const assessmentsResponse = await adminAssessmentAPI.getAllAssessments();
            const assessments = assessmentsResponse.data?.assessments || assessmentsResponse.data || [];
            totalAssessments = Array.isArray(assessments) ? assessments.length : 0;
          } catch (error) {
            console.error('Error fetching assessments:', error);
          }

          // Fetch pending requests count
          let pendingRequests = 0;
          try {
            const requestStatsResponse = await adminAPI.getEmployerRequestStats();
            const requestStats = requestStatsResponse.data;
            pendingRequests = requestStats.pendingRequests || 0;
          } catch (error) {
            console.error('Error fetching request stats:', error);
          }

          setKipMetrics({
            totalUsers,
            activeCandidates,
            totalAssessments,
            pendingRequests,
            isLoading: false
          });
        } catch (error) {
          console.error('Error fetching KIP metrics:', error);
          setKipMetrics(prev => ({ ...prev, isLoading: false }));
        }
      }
    };

    fetchKipMetrics();
  }, [user, adminView]);

  // Fetch employers list and AI subscription details
  useEffect(() => {
    const fetchAdminData = async () => {
      if (user?.userType === 'admin' && adminView === 'overview') {
        // Fetch employers list
        setLoadingEmployersList(true);
        try {
          const employersResponse = await adminAPI.getEmployers(1, 10, '');
          const employers = employersResponse.data?.employers || employersResponse.data || [];
          setEmployersList(Array.isArray(employers) ? employers : []);
        } catch (error) {
          console.error('Error fetching employers:', error);
          setEmployersList([]);
        } finally {
          setLoadingEmployersList(false);
        }

        // Fetch AI subscription details
        setLoadingSubscription(true);
        try {
          const subscriptionResponse = await adminAPI.getAISubscriptionDetails();
          setAiSubscription(subscriptionResponse.data);
        } catch (error) {
          console.error('Error fetching AI subscription details:', error);
          // Set default structure if API doesn't exist yet
          setAiSubscription({
            status: 'Unknown',
            plan: 'N/A',
            usage: 'N/A',
            expiresAt: null
          });
        } finally {
          setLoadingSubscription(false);
        }
      }
    };

    fetchAdminData();
  }, [user, adminView]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-secondary-900">Admin Dashboard</h2>
        <span className="badge-primary">
          System Administrator
        </span>
      </div>

      {/* Admin Navigation Tabs — Overview, Users, Employers Request, Candidates Requests, Assessments, Skill Assignment, Plans, Courses, Reports */}
      <nav
        className="flex flex-wrap items-end gap-x-1 gap-y-0 border-b border-secondary-200 overflow-x-auto"
        aria-label="Admin sections"
      >
        <button
          type="button"
          onClick={() => setAdminView('overview')}
          className={`underline-tab ${
            adminView === 'overview' ? 'underline-tab-active' : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Overview
        </button>
        <button
          type="button"
          onClick={() => setAdminView('users')}
          className={`underline-tab ${
            adminView === 'users'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Users
        </button>
        <button
          type="button"
          onClick={() => setAdminView('employer-requests')}
          className={`underline-tab ${
            adminView === 'employer-requests'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Employers Request
        </button>
        <button
          type="button"
          onClick={() => setAdminView('candidate-requests')}
          className={`underline-tab ${
            adminView === 'candidate-requests'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Candidates Requests
        </button>
        <button
          type="button"
          onClick={() => setAdminView('assessments')}
          className={`underline-tab ${
            adminView === 'assessments'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          Assessments
        </button>
        <button
          type="button"
          onClick={() => setAdminView('skill-assignment')}
          className={`underline-tab ${
            adminView === 'skill-assignment'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          Skill Assignment
        </button>
        <button
          type="button"
          onClick={() => setAdminView('subscription-plans')}
          className={`underline-tab ${
            adminView === 'subscription-plans'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          Plans
        </button>
        <button
          type="button"
          onClick={() => setAdminView('courses')}
          className={`underline-tab ${
            adminView === 'courses'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="inline-flex items-center gap-1">
            Courses
            {generationJobs.some((j) => j.status === 'running') && (
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full bg-amber-400 animate-pulse"
                title="Course generation in progress"
                aria-hidden
              />
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setAdminView('reports')}
          className={`underline-tab ${
            adminView === 'reports'
              ? 'underline-tab-active'
              : ''
          }`}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Reports
        </button>
      </nav>

      {/* Conditional Content Based on Active Tab */}
      {adminView === 'users' ? (
        <UserManagement />
      ) : adminView === 'employer-requests' ? (
        <EmployerRequests />
      ) : adminView === 'candidate-requests' ? (
        <CandidateDetailRequests />
      ) : adminView === 'assessments' ? (
        <AdminAssessmentManagement />
      ) : adminView === 'courses' ? (
        <AdminCourseManagement
          generationJobs={generationJobs}
          updateGenerationJob={updateGenerationJob}
          dismissGenerationJob={dismissGenerationJob}
          appendGenerationJob={appendGenerationJob}
        />
      ) : adminView === 'reports' ? (
        <AdminReportsManagement />
      ) : adminView === 'skill-assignment' ? (
        <CandidateSkillAssignment />
      ) : adminView === 'subscription-plans' ? (
        <EmployerSubscriptionPlans />
      ) : (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="ui-card p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-secondary-600">Total Users</h3>
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <p className="text-3xl font-bold text-secondary-900">
                {kipMetrics.isLoading ? '...' : kipMetrics.totalUsers.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-1">Employees and candidates</p>
            </div>
            
            <div className="ui-card p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-secondary-600">Candidate Active</h3>
                <svg className="w-8 h-8 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-3xl font-bold text-secondary-900">
                {kipMetrics.isLoading ? '...' : kipMetrics.activeCandidates.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total no of active candidates</p>
            </div>
            
            <div className="ui-card p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-secondary-600">Assessments</h3>
                <svg className="w-8 h-8 text-info-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <p className="text-3xl font-bold text-secondary-900">
                {kipMetrics.isLoading ? '...' : kipMetrics.totalAssessments.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total assessments available</p>
            </div>
            
            <div className="ui-card p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-secondary-600">Pending Requests</h3>
                <svg className="w-8 h-8 text-warning-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-3xl font-bold text-secondary-900">
                {kipMetrics.isLoading ? '...' : kipMetrics.pendingRequests.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-1">Employer registration requests</p>
            </div>
          </div>

          {/* Recent Employers and AI Subscription Section - Side by Side */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Employers Section */}
            <div className="ui-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Employers</h3>
              {loadingEmployersList ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : employersList.length > 0 ? (
                <div className="space-y-4">
                  {employersList.slice(0, 5).map((employer) => (
                    <div key={employer.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-900">
                            {employer.firstName} {employer.lastName}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">{employer.email}</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-sm text-gray-700">
                              <span className="font-medium">Company:</span> {employer.companyName}
                            </p>
                            {employer.industry && (
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">Industry:</span> {employer.industry}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                            employer.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {employer.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {employer.isVerified && (
                            <span className="px-3 py-1 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No employers found</p>
              )}
            </div>

            {/* AI Subscription Section */}
            <div className="ui-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Subscription Status</h3>
              {loadingSubscription ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : aiSubscription ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="text-lg font-semibold text-gray-900">{aiSubscription.status || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Plan</p>
                    <p className="text-lg font-semibold text-gray-900">{aiSubscription.plan || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Usage</p>
                    <p className="text-lg font-semibold text-gray-900">{aiSubscription.usage || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Expires At</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {aiSubscription.expiresAt ? new Date(aiSubscription.expiresAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="mt-4 text-sm text-gray-500">Subscription details not available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

